/**
 * RecentBlueprintsPanel — small "your recent N saved [type] briefs"
 * affordance. Mounted at the top of each Blueprint generator page; if
 * the user has saved generations of this type, they see a quick-access
 * list with metadata + a one-line preview. Clicking a row opens that
 * specific saved blueprint in the same render path the just-generated
 * one would use (see ProfileDetail's auto-load pattern from #74).
 *
 * Per #78 (Russ 2026-05-09): rolls equivalent of OM auto-load (#74) to
 * the non-profile-anchored Blueprint surfaces — CM, Pre-Meeting,
 * Resume, Relational, Team. Shape differs from OM's: those surfaces
 * have multiple inputs and start fresh each time, so "auto-load
 * latest" isn't always the right UX. A list of recent + click-to-open
 * is more honest about how those surfaces actually get used.
 *
 * Hidden when the user has zero saved blueprints of this type — the
 * generator form takes over the surface as before.
 */

import { useEffect, useState } from "react";
import { parseJsonResponse } from "../lib/api-fetch.js";
import Callout from "./Callout.jsx";

const TYPE_LABEL = {
  operating_manual: "Playbook",
  pre_meeting: "Pre-Meeting Brief",
  resume: "Counterpart Breakdown",          // user-facing rename per LOCKED 2026-05-25; internal key "resume" stays
  relational: "Relationship Read",
  team: "Room Blueprint",
};

/**
 * @param {object} props
 * @param {string} props.type — blueprint_type to filter by
 * @param {string} props.bearerToken — auth token for the API call
 * @param {(row) => void} [props.onOpen] — optional callback when user clicks a row;
 *   if not provided, the panel just lists metadata (no click-to-open).
 *   When wired, the parent typically uses the row's blueprint_id to load
 *   the saved blueprint into its own state, mirroring the just-generated
 *   render path.
 * @param {number} [props.limit=5] — how many to show
 */
export default function RecentBlueprintsPanel({ type, modeVariant, bearerToken, onOpen, limit = 5 }) {
  const [rows, setRows] = useState(null);  // null = loading, [] = none, [...] = list
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bearerToken || !type) return;
    let active = true;
    const qs = new URLSearchParams({ type, limit: String(limit) });
    if (modeVariant) qs.set("mode_variant", modeVariant);
    fetch(`/.netlify/functions/blueprints-recent?${qs.toString()}`, {
      headers: { authorization: `Bearer ${bearerToken}` },
    })
      .then(async (r) => ({ res: r, body: await parseJsonResponse(r) }))
      .then(({ res, body }) => {
        if (!active) return;
        if (!res.ok) {
          setError(body.error || `Failed to load (HTTP ${res.status})`);
          setRows([]);
          return;
        }
        setRows(body.blueprints || []);
      })
      .catch((e) => {
        if (!active) return;
        setError(e.message);
        setRows([]);
      });
    return () => { active = false; };
  }, [type, modeVariant, bearerToken, limit]);

  // Hidden states — don't render anything that visually distracts when
  // there's nothing to show. The form below takes over.
  if (rows === null) return null;          // loading — silent
  if (error) return null;                  // error — silent (form still works)
  if (rows.length === 0) return null;      // empty — silent (first-time UX)

  const label = TYPE_LABEL[type] || "Blueprint";

  return (
    <div data-component="RecentBlueprintsPanel" style={{ marginBottom: 24 }}>
      <Callout.Tip>
        <div style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: "var(--ppr-typography-font-size-sm)", textTransform: "uppercase", letterSpacing: "var(--ppr-typography-letter-spacing-wider)", color: "var(--ppr-color-text-secondary)", marginBottom: 8 }}>
          Your recent {label}s
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {rows.map((row) => (
            <li
              key={row.blueprint_id}
              style={{
                padding: "8px 0",
                borderBottom: "1px solid var(--ppr-color-border-soft, var(--ppr-color-border-default))",
                display: "flex",
                gap: 12,
                alignItems: "baseline",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "11px", color: "var(--ppr-color-text-muted)", marginBottom: 2 }}>
                  {formatTimestamp(row.generated_at)}
                  {row.mode_variant ? ` · ${row.mode_variant.replace(/_/g, " ")}` : ""}
                </div>
                {row.summary_first_line ? (
                  <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "var(--ppr-typography-font-size-sm)", color: "var(--ppr-color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.summary_first_line}
                  </div>
                ) : (
                  <div style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "11px", color: "var(--ppr-color-text-muted)", fontStyle: "italic" }}>
                    (no preview available)
                  </div>
                )}
              </div>
              {onOpen && (
                <button
                  type="button"
                  onClick={() => onOpen(row)}
                  data-testid="recent-blueprint-open"
                  style={{
                    fontFamily: "var(--ppr-typography-font-family-display)",
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    padding: "6px 12px",
                    background: "var(--ppr-color-accent-navy)",
                    color: "var(--ppr-color-text-inverse)",
                    border: "1px solid var(--ppr-color-accent-navy)",
                    borderRadius: "var(--ppr-radius-sm)",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  Open
                </button>
              )}
            </li>
          ))}
        </ul>
      </Callout.Tip>
    </div>
  );
}

function formatTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // "May 9, 1:36 PM" — short, readable
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
